import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { COLLECTORS } from '@/data/collectors';
import type { Collector, LastSyncStatus } from '@/data/collectors';
import { DEFAULT_POLL_INTERVAL_MS, computeNextSyncInS } from '@/lib/nextSync';

interface UseCollectorsResult {
  data: readonly Collector[];
  isLoading: boolean;
}

interface ApiLastSync {
  status: string;
  startedAt: string;
  finishedAt: string | null;
  recordsFetched: number;
  recordsUpserted: number;
}

interface ApiCollector {
  id: string;
  kind: string;
  displayName: string;
  enabled: boolean;
  createdAt: string;
  pollIntervalMsOverride?: number | null;
  lastSync: ApiLastSync | null;
}

function normalizeLastSyncStatus(raw: string | undefined): LastSyncStatus {
  if (raw === 'success' || raw === 'partial' || raw === 'failed') return raw;
  return null;
}

function deriveStatus(enabled: boolean, lastSyncStatus: LastSyncStatus): Collector['status'] {
  // Q6 (parent task 105): a failed last sync promotes the card to 'error'
  // even when the collector is still enabled — the problem is the data
  // flow, not the config. Disconnected (enabled=false) stays disconnected.
  if (lastSyncStatus === 'failed') return 'error';
  return enabled ? 'connected' : 'disconnected';
}

export function useCollectors(): UseCollectorsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['collectors', orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{ collectors: ApiCollector[] }>(
        `/api/v1/orgs/${orgId}/collectors`,
        token,
      );
      const nowMs = Date.now();
      return res.collectors.map((c): Collector => {
        const lastSyncStatus = normalizeLastSyncStatus(c.lastSync?.status);
        const finishedOrStarted = c.lastSync?.finishedAt ?? c.lastSync?.startedAt ?? '';
        const pollMs = c.pollIntervalMsOverride ?? DEFAULT_POLL_INTERVAL_MS;
        return {
          id: c.id,
          name: c.displayName,
          type: c.kind as 'insightful' | 'manual',
          status: deriveStatus(c.enabled, lastSyncStatus),
          lastSyncAt: finishedOrStarted,
          nextSyncInS: computeNextSyncInS(c.lastSync?.finishedAt ?? null, pollMs, nowMs),
          connectedAt: c.createdAt,
          recordsFetched: c.lastSync?.recordsFetched ?? null,
          recordsUpserted: c.lastSync?.recordsUpserted ?? null,
          lastSyncStatus,
        };
      });
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: COLLECTORS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Toggle the `enabled` flag on a collector via
 * `PATCH /api/v1/orgs/:orgId/collectors/:id`. Admin-only surface (task
 * 105d D3). Returns the React-Query mutation so callers can add
 * optimistic-update / rollback behaviour at the component layer.
 */
export function useUpdateCollector() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${orgId}/collectors/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['collectors', orgId] });
    },
  });
}

/**
 * Archive a collector via `POST /collectors/:id/archive` (task 109 D3).
 * Admin-only. Archived collectors are hidden from the default list
 * endpoint and excluded from worker polling. See ADR 0026.
 */
export function useArchiveCollector() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${orgId}/collectors/${id}/archive`, token, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['collectors', orgId] });
    },
  });
}

/**
 * Enqueue a one-shot manual sync via
 * `POST /collectors/:id/sync` (task 109 D4). Admin-only. Returns a
 * jobId + queued status so the caller can poll for completion.
 */
export interface SyncNowResponse {
  jobId: string;
  status: 'queued';
}

export function useSyncCollector() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<SyncNowResponse> => {
      const token = await getToken();
      return apiFetch<SyncNowResponse>(`/api/v1/orgs/${orgId}/collectors/${id}/sync`, token, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      // Optimistically kick the list query; the polling mechanism in the
      // card drives the per-collector refresh while the sync is in flight.
      void qc.invalidateQueries({ queryKey: ['collectors', orgId] });
    },
  });
}
